import { Button } from "@/components/ui/button";
import { useNavigate, NavLink } from "react-router-dom";
import { supabase } from "@/lib/supabase/client";
import { LogOut, Home, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMemo, useState } from "react";
import { useAppContext } from "@/contexts/AppContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";

export default function PortalNav() {
  const navigate = useNavigate();
  const { setLanguage, t } = useLanguage();

  const { isActive, session, profile, isDemo } = useAppContext();
  const { impersonating } = useImpersonation();
  // When a top banner is visible (impersonation or demo), push the nav down by its height (h-10 = 40px)
  const navTop = (impersonating || isDemo) ? "top-10" : "top-0";
  const [signingOut, setSigningOut] = useState(false);


  const logoUrl = "/fyrris-logo.png";

  const handleSignOut = async () => {
    try {
      setSigningOut(true);
      await supabase.auth.signOut();
      navigate("/");
    } finally {
      setSigningOut(false);
    }
  };

  const portalLinks = useMemo(() => {
    if (!isActive) {
      return [
        { to: "/portal/onboarding", label: "Onboarding" },
        { to: "/portal/billing", label: "Activate" },
      ];
    }
    return [
      { to: "/portal", label: "Dashboard", end: true },
      { to: "/portal/products", label: "Products" },
      { to: "/portal/locations", label: "Locations" },
      { to: "/portal/catalogue", label: "Catalogue" },
      { to: "/portal/quote-requests", label: "Quote Requests" },
      { to: "/portal/embed",    label: "Embed" },
      { to: "/portal/metrics",  label: "Metrics" },
      { to: "/portal/settings", label: "Settings" },
      { to: "/portal/account", label: "Account" },
      { to: "/portal/billing", label: "Billing" },
      { to: "/portal/support", label: "Support" },
    ];
  }, [isActive]);

const LinkPill = ({
    to,
    label,
    end,
  }: {
    to: string;
    label: string;
    end?: boolean;
  }) => (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition",
          isActive ? "bg-gray-800 text-white" : "text-gray-300 hover:bg-gray-800/60",
        ].join(" ")
      }
    >
      {label}
    </NavLink>
  );

  return (
    <header
      id="portal-nav"
      className={`fixed ${navTop} left-0 w-full h-14 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-[1000] isolate`}
    >
      {/* three regions: left (logo), center (links), right (actions) */}
      <nav className="mx-auto flex h-full w-full max-w-7xl items-center gap-3 px-3 sm:px-4">
        {/* Left: Logo + Title */}
        <button
          type="button"
          onClick={() => navigate("/portal")}
          className="flex shrink-0 items-center gap-2 sm:gap-3"
          aria-label="Portal Home"
        >
          <img
            src={logoUrl}
            alt="Fyrris"
            className="h-6 w-auto max-w-[120px] object-contain sm:h-7"
          />
          <span className="hidden text-sm font-semibold text-white sm:inline">
            {t("nav.retailer") || "Retailer Portal"}
          </span>
        </button>

        {/* Center: Portal links (scroll horizontally if cramped) */}
        <div className="min-w-0 flex-1 overflow-x-auto">
          <div className="flex min-w-max items-center gap-1.5 pl-1">
            {portalLinks.map((l) => (
              <LinkPill key={l.to} to={l.to} label={l.label} end={l.end} />
            ))}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Change language" className="h-9 w-9">
                <Globe className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8}>
              <DropdownMenuItem onClick={() => setLanguage("en")}>English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage("fr")}>Français</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage("es")}>Español</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            aria-label="Public site"
            className="h-9"
          >
            <Home className="mr-0 h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("nav.home") || "Home"}</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            disabled={signingOut}
            aria-label="Sign out"
            className="h-9"
          >
            <LogOut className="mr-0 h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("nav.signOut") || "Sign out"}</span>
          </Button>
        </div>
      </nav>
    </header>
  );
}
